<script>
    import { createEventDispatcher, onDestroy, onMount } from 'svelte'
    import { fade } from 'svelte/transition'
    import { toast } from 'bulma-toast'

    import DateMethod from '../$methods/date.method'
    import ShipmentsService from '../$services/shipments.service'

    export let shipmentId

    const dispatch = createEventDispatcher()

    let loading = true
    let downloading = false
    let shipment = null
    let podLink

    onMount(() => {
        window.addEventListener('keydown', onKey)
        load()
    })

    onDestroy(() => window.removeEventListener('keydown', onKey))

    function onKey(event) {
        if (event.key === 'Escape') close()
    }

    function close() {
        dispatch('close')
    }

    async function load() {
        loading = true
        const resp = await ShipmentsService.getShipmentDelivery(shipmentId)
        loading = false

        if (!resp || !resp.success) {
            toast({ message: (resp && resp.error && resp.error.message) || 'No se pudo consultar la entrega', type: 'is-danger' })
            close()
            return
        }

        shipment = resp.data
    }

    function imageSrc(value) {
        if (!value) return ''
        if (value.startsWith('data:') || value.startsWith('http://') || value.startsWith('https://')) return value
        return `data:image/png;base64,${value}`
    }

    async function downloadPod() {
        downloading = true
        const blob = await ShipmentsService.getShipmentSignature(shipmentId)
        downloading = false

        if (!blob || !blob.size || (blob.type && blob.type.indexOf('json') >= 0)) {
            toast({ message: 'No se pudo descargar el POD', type: 'is-danger' })
            return
        }

        podLink.download = `${(shipment && shipment.guide) || 'pod'}.pdf`
        podLink.href = window.URL.createObjectURL(blob)
        podLink.click()
    }
</script>

<a bind:this={podLink} style="display: none;" href="/">.</a>

<div class="modal is-active delivery" in:fade={{ duration: 200 }} out:fade={{ duration: 100 }}>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div class="modal-background" on:click={close}></div>
    <div class="modal-card">
        <header class="modal-card-head">
            <p class="modal-card-title">{ (shipment && shipment.guide) || 'Entrega' }</p>
            <button type="button" class="delete" aria-label="Cerrar" on:click={close}></button>
        </header>
        <section class="modal-card-body">
            {#if loading}
                <p>Cargando…</p>
            {:else if shipment}
                <div class="proof">
                    <div>
                        <h3>Evidencias</h3>
                        {#if shipment.evidence || shipment.evidence_2}
                            <div class="photos">
                                {#if shipment.evidence}
                                    <a href={shipment.evidence} target="_blank" rel="noreferrer">
                                        <img src={shipment.evidence} alt="Evidencia de entrega" />
                                    </a>
                                {/if}
                                {#if shipment.evidence_2}
                                    <a href={shipment.evidence_2} target="_blank" rel="noreferrer">
                                        <img src={shipment.evidence_2} alt="Segunda evidencia de entrega" />
                                    </a>
                                {/if}
                            </div>
                        {:else}
                            <p class="missing">Sin evidencia</p>
                        {/if}
                    </div>
                    <div>
                        <h3>Firma</h3>
                        {#if shipment.signature}
                            <img class="signature" src={imageSrc(shipment.signature)} alt="Firma de recibido" />
                        {:else}
                            <p class="missing">Sin firma</p>
                        {/if}
                    </div>
                </div>

                <h3>POD</h3>
                <div class="pod">
                    <button type="button" class="button is-outlined" class:is-loading={downloading} on:click={downloadPod}>
                        Descargar POD
                    </button>
                    {#if shipment.pod}
                        <a href={shipment.pod} target="_blank" rel="noreferrer">Abrir POD</a>
                    {/if}
                </div>

                <h3>Tracking</h3>
                {#if shipment.tracking && shipment.tracking.length}
                    <div class="timeline">
                        <header class="timeline-header">
                            <span class="tag is-medium is-light">Inicio</span>
                        </header>
                        {#each shipment.tracking as event}
                            <div class="timeline-item">
                                <div class="timeline-marker is-info"></div>
                                <div class="timeline-content">
                                    <p class="heading">{ event.name || 'Sin estatus' }</p>
                                    <div>{ event.details || 'Sin detalles' }</div>
                                    <small>{ event.created ? DateMethod.centralDateTimeLarge(event.created) : '—' }</small>
                                    {#if event.driver}
                                        <div>{ event.driver.name }</div>
                                    {/if}
                                    {#if event.location}
                                        <a target="_blank" rel="noreferrer" href="https://www.google.com/maps/search/?api=1&query={event.location.lat},{event.location.lng}">Ver ubicación</a>
                                    {/if}
                                </div>
                            </div>
                        {/each}
                        <div class="timeline-header">
                            <span class="tag is-medium is-light">Final</span>
                        </div>
                    </div>
                {:else}
                    <p class="missing">Sin tracking</p>
                {/if}
            {/if}
        </section>
    </div>
</div>

<style>
    .delivery {
        z-index: 40;
    }

    .modal-card {
        width: 720px;
        max-width: calc(100vw - 2rem);
    }

    .modal-card-body {
        max-height: 75vh;
    }

    h3 {
        margin: 1.25rem 0 0.5rem;
        font-size: 1rem;
        font-weight: 700;
    }

    .proof {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
        align-items: start;
    }

    .proof h3 {
        margin-top: 0;
    }

    .photos {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .photos a,
    .signature {
        display: block;
    }

    img {
        width: 100%;
        max-height: 280px;
        object-fit: contain;
        background: #f4f4f4;
        border-radius: 6px;
    }

    .signature {
        background: #fff;
        border: 1px solid #e6e6e6;
    }

    .missing {
        color: #888;
    }

    .pod {
        display: flex;
        align-items: center;
        gap: 1rem;
    }
</style>
